import { useState } from 'react';
import { Download, Heart, PencilRuler, Save, Shuffle, X } from 'lucide-react';
import clsx from 'clsx';
import { useBatch } from '../../store/batchStore';
import { useStudio } from '../../store/studioStore';
import { serialisePreset } from '../../core/export/preset';
import { savePreset } from '../../core/export/presetStore';
import { downloadBlob, formatFilename } from '../../core/export/download';
import { renderOne } from '../../bulk/engine';
import type { Variant } from '../../bulk/types';

/** Full view of one variant: the image, everything that produced it, and what to do next. */
export function VariantDetail({ variant, onClose }: { variant: Variant; onClose: () => void }): JSX.Element {
  const replaceDocument = useStudio((s) => s.replaceDocument);
  const toggleFavourite = useBatch((s) => s.toggleFavourite);
  const setConfig = useBatch((s) => s.setConfig);
  const config = useBatch((s) => s.config);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);

  const flash = (text: string): void => {
    setNote(text);
    setTimeout(() => setNote(null), 2400);
  };

  const exportFull = async (): Promise<void> => {
    setBusy(true);
    try {
      const { blob } = await renderOne(variant.doc, config.exportScale, 'png', 1);
      const name = formatFilename('{word}_{seed}_{index}', {
        word: variant.meta.word,
        seed: variant.meta.seed,
        index: String(variant.meta.index).padStart(4, '0'),
      });
      downloadBlob(blob, `${name}.png`);
      flash(`${(blob.size / 1024 / 1024).toFixed(2)} MB exportiert`);
    } catch (err) {
      flash((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[110] flex bg-black/80" onClick={onClose}>
      <div className="flex min-w-0 flex-1 items-center justify-center p-8" onClick={(e) => e.stopPropagation()}>
        {variant.url ? (
          <img src={variant.url} alt="" className="max-h-full max-w-full object-contain shadow-2xl shadow-black/60" />
        ) : (
          <p className="text-[12px] text-amber-400">{variant.error ?? 'Kein Bild'}</p>
        )}
      </div>

      <aside
        className="flex w-[340px] shrink-0 flex-col border-l border-ink-800 bg-ink-900"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-2 border-b border-ink-800 px-3 py-2.5">
          <span className="flex-1 font-mono text-[12px] text-ink-100">
            Variante {String(variant.meta.index).padStart(3, '0')}
          </span>
          <button
            type="button"
            onClick={() => toggleFavourite(variant.id)}
            className={clsx('ghost-button h-6 w-6 p-0', variant.favourite && 'text-red-400')}
            title="Favorit"
          >
            <Heart size={12} fill={variant.favourite ? 'currentColor' : 'none'} />
          </button>
          <button type="button" onClick={onClose} className="ghost-button h-6 w-6 p-0">
            <X size={13} />
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-3 py-2">
          <dl className="space-y-1 font-mono text-[10px]">
            <Field label="Wort" value={variant.meta.word} />
            <Field label="Seed" value={String(variant.meta.seed)} />
            <Field label="Schrift" value={variant.meta.fontId} />
            <Field label="Techniken" value={variant.meta.techniques.join(' + ') || 'nur Text'} />
            <Field label="Palette" value={variant.meta.palette} />
            <Field label="Canvas" value={variant.meta.canvas} />
            <Field label="Vorschau" value={`${variant.width}×${variant.height}`} />
            <Field label="Renderzeit" value={`${Math.round(variant.ms)} ms`} />
            <Field label="Elemente" value={variant.elements.toLocaleString('de-CH')} />
          </dl>

          {Object.keys(variant.meta.dimensions).length > 0 && (
            <>
              <div className="panel-label mt-3 pb-1">Variierte Parameter</div>
              <dl className="space-y-1 font-mono text-[10px]">
                {Object.entries(variant.meta.dimensions).map(([key, value]) => (
                  <Field
                    key={key}
                    label={key}
                    value={typeof value === 'number' ? String(Number(value.toFixed(4))) : String(value)}
                  />
                ))}
              </dl>
            </>
          )}
        </div>

        {note && <p className="px-3 pb-1 font-mono text-[10px] text-accent-500">{note}</p>}

        <div className="grid grid-cols-2 gap-1 border-t border-ink-800 p-2">
          <Action
            icon={<PencilRuler size={11} />}
            label="Im Studio öffnen"
            onClick={() => {
              replaceDocument(structuredClone(variant.doc), `Variante ${variant.meta.index}`);
              onClose();
              flash('Ins Studio geladen.');
            }}
          />
          <Action
            icon={<Shuffle size={11} />}
            label="Als Mutationsbasis"
            onClick={() => {
              setConfig({ strategy: 'mutation', mutationBaseId: variant.id });
              onClose();
            }}
          />
          <Action
            icon={<Save size={11} />}
            label="Als Preset"
            onClick={() =>
              void savePreset(serialisePreset(variant.doc, `Variante ${variant.meta.index}`)).then(() =>
                flash('Als Preset gespeichert.'),
              )
            }
          />
          <Action
            icon={<Download size={11} />}
            label={busy ? 'Rendert…' : `PNG ${config.exportScale}×`}
            disabled={busy}
            onClick={() => void exportFull()}
          />
        </div>
      </aside>
    </div>
  );
}

function Field({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <div className="flex gap-2">
      <dt className="w-[92px] shrink-0 truncate text-ink-500">{label}</dt>
      <dd className="min-w-0 flex-1 break-words text-ink-200">{value}</dd>
    </div>
  );
}

function Action({
  icon,
  label,
  onClick,
  disabled,
}: {
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
  disabled?: boolean;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="flex items-center justify-center gap-1.5 rounded-md bg-ink-800 py-2 text-[10px] text-ink-200 transition-colors hover:bg-ink-750 disabled:opacity-50"
    >
      {icon}
      {label}
    </button>
  );
}
